import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { useState, useEffect } from 'react';
import { deepPurple } from '@mui/material/colors';
import { errorAlert, successAlert } from 'ui-component/Toasts';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';

const UpdateNotification = ({ notification, notificationTypes, customerLoginUserNameOptions, onUpdate, userNameOptions, redirectTypes }) => {
    const token = useSelector(selectCurrentToken);

    const [open, setOpen] = useState(false);

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [notificationTypeID, setNotificationTypeID] = useState();
    const [csUserName, setCsUserName] = useState('');
    const [customerLoginUserName, setCustomerLoginUserName] = useState('');
    const [notificationRedirectTypeID, setNotificationRedirectTypeID] = useState();
    const [externalUrl, setExternalUrl] = useState('');

    const [localizedTitle, setLocalizedTitle] = useState('');
    const [localizedContent, setLocalizedContent] = useState('');

    const [loading, setLoading] = useState(false);

    // Populate form with existing notification data
    useEffect(() => {
        if (notification) {
            setTitle(notification.title || '');
            setContent(notification.content || '');
            setNotificationTypeID(notification.notificationTypeID);
            setCsUserName(notification.csUserName || '');
            setCustomerLoginUserName(notification.customerLoginUserName || '');

            if (notification.localizations && notification.localizations.languageID === 4) {
                setLocalizedTitle(notification.localizations.title || '');
                setLocalizedContent(notification.localizations.content || '');
            }

            setNotificationRedirectTypeID(notification.notificationRedirectTypeID);
            setExternalUrl(notification.externalUrl || '');
        }
    }, [notification]);

    const handleUpdate = async () => {
        const valid = validate();
        if (!valid) {
            errorAlert('Please fill all required fields.');
            return;
        }

        // Base data object
        const data = {
            id: notification.id,
            title,
            content,
            notificationTypeID,
            localizations: [
                {
                    title: localizedTitle,
                    content: localizedContent,
                    languageID: 4
                }
            ],
            notificationRedirectTypeID: notificationRedirectTypeID,
            externalUrl: externalUrl
        };

        // Add additional fields based on notification type
        if (notificationTypeID === 2) {
            data.csUserName = csUserName;
        } else if (notificationTypeID === 3) {
            data.customerLoginUserName = customerLoginUserName;
        }

        setLoading(true);
        try {
            const res = await axiosInstance.put(`/Notification`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                successAlert('Notification updated.');
                onUpdate && onUpdate();
                setOpen(false);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
            errorAlert('Failed to update notification.');
        } finally {
            setLoading(false);
        }
    };

    const validate = () => {
        let valid = true;
        if (!title || !content || !notificationTypeID || !localizedTitle || !localizedContent) {
            valid = false;
        }
        if (notificationTypeID === 2 && !csUserName) {
            valid = false;
        }
        if (notificationTypeID === 3 && !customerLoginUserName) {
            valid = false;
        }
        return valid;
    };

    return (
        <>
            <Button
                variant="contained"
                sx={{
                    display: 'block',
                    width: '100px',
                    backgroundColor: deepPurple[500]
                }}
                onClick={() => setOpen(true)}
            >
                Update
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth={'md'}>
                <DialogTitle>Update Notification</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} marginTop={1}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                name="title"
                                label="Title"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                multiline
                                required
                                name="content"
                                label="Content"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="notification-type">Notification Type</InputLabel>
                                <Select
                                    labelId="notification-type"
                                    value={notificationTypeID}
                                    label="Notification Type"
                                    onChange={(e) => {
                                        setCsUserName('');
                                        setCustomerLoginUserName('');
                                        setNotificationTypeID(e.target.value);
                                    }}
                                    fullWidth
                                >
                                    {notificationTypes?.map((notificationType) => (
                                        <MenuItem key={notificationType.id} value={notificationType.id}>
                                            {notificationType.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                name="csUsername"
                                label="CS Username"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={csUserName}
                                onChange={(e) => setCsUserName(e.target.value)}
                                disabled={notificationTypeID === 1 || notificationTypeID === 3}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                freeSolo
                                fullWidth
                                value={customerLoginUserName}
                                onChange={(event, newValue) => {
                                    setCustomerLoginUserName(newValue);
                                }}
                                inputValue={customerLoginUserName}
                                onInputChange={(event, newInputValue) => {
                                    setCustomerLoginUserName(newInputValue);
                                }}
                                options={customerLoginUserNameOptions}
                                renderInput={(params) => <TextField label="Customer Login Username" {...params} />}
                                disableClearable
                                disabled={notificationTypeID === 1 || notificationTypeID === 2}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="notification-redirect-type">Notification Redirect Type</InputLabel>
                                <Select
                                    labelId="notification-redirect-type"
                                    value={notificationRedirectTypeID}
                                    label="Notification Redirect Type"
                                    onChange={(e) => {
                                        setExternalUrl('');
                                        setNotificationRedirectTypeID(e.target.value);
                                    }}
                                    fullWidth
                                >
                                    {redirectTypes?.map((notificationType) => (
                                        <MenuItem key={notificationType.id} value={notificationType.id}>
                                            {notificationType.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                name="externalUrl"
                                label="External Url"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={externalUrl}
                                onChange={(e) => setExternalUrl(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6">Localization</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                name="title"
                                label="Title"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={localizedTitle}
                                onChange={(e) => setLocalizedTitle(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                multiline
                                required
                                name="content"
                                label="Content"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={localizedContent}
                                onChange={(e) => setLocalizedContent(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdate} disabled={loading}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpdateNotification;
